import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { adminRequest, getAdminHeaders } from "./adminApi";

const CATEGORY_OPTIONS = [
  { value: "system", label: "系统通知" },
  { value: "audit", label: "认证相关" },
  { value: "reminder", label: "资料提醒" },
];

const LINK_PRESETS = [
  { label: "无跳转", value: "" },
  { label: "我的认证", value: "/center/verify" },
  { label: "会员资料", value: "/center/profile" },
  { label: "咨询师资料", value: "/consultant/profile/setup" },
];

export default function AdminSystemMessages() {
  const location = useLocation();
  const preset = location.state || {};
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [recipientType, setRecipientType] = useState(preset.recipientType || "user");
  const [recipientId, setRecipientId] = useState(
    preset.recipientId ? String(preset.recipientId) : ""
  );
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("system");
  const [linkUrl, setLinkUrl] = useState("");
  const [sending, setSending] = useState(false);

  const pageSize = 20;

  const loadHistory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (filterType) params.set("recipient_type", filterType);

      const { res, data } = await adminRequest(
        `/api/admin/system-messages?${params}`,
        { headers: getAdminHeaders() }
      );

      if (res.ok) {
        setHistory(data.items || []);
        setTotal(data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, [page, filterType]);

  const handleSend = async (e) => {
    e.preventDefault();
    const id = Number(recipientId);
    if (!id) {
      alert("请填写接收人 ID");
      return;
    }
    if (!title.trim() || !content.trim()) {
      alert("请填写标题和内容");
      return;
    }

    setSending(true);
    try {
      const { res, data } = await adminRequest("/api/admin/system-messages", {
        method: "POST",
        headers: getAdminHeaders(true),
        body: JSON.stringify({
          recipient_type: recipientType,
          recipient_id: id,
          title: title.trim(),
          content: content.trim(),
          category,
          link_url: linkUrl.trim() || null,
        }),
      });

      if (!res.ok) {
        alert(data?.detail || "发送失败");
        return;
      }

      setMessage("系统消息已发送");
      setTitle("");
      setContent("");
      setRecipientId("");
      setPage(1);
      loadHistory();
    } catch (err) {
      console.error(err);
      alert("发送失败");
    } finally {
      setSending(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">系统消息</h1>
        <p className="text-sm text-gray-500 mt-1">
          向会员或咨询师发送认证提醒、资料完善通知等站内消息。
        </p>
      </div>

      {message && (
        <div className="bg-green-50 text-green-700 text-sm px-4 py-2 rounded-lg">
          {message}
        </div>
      )}

      <form
        onSubmit={handleSend}
        className="bg-white rounded-2xl shadow p-6 space-y-4"
      >
        <h2 className="text-lg font-bold text-gray-800">发送新消息</h2>

        <div className="grid md:grid-cols-2 gap-4">
          <label className="block text-sm">
            <span className="text-gray-500">接收类型</span>
            <select
              value={recipientType}
              onChange={(e) => setRecipientType(e.target.value)}
              className="mt-1 w-full border rounded-xl px-4 py-2"
            >
              <option value="user">会员</option>
              <option value="consultant">咨询师</option>
            </select>
          </label>

          <label className="block text-sm">
            <span className="text-gray-500">接收人 ID</span>
            <input
              value={recipientId}
              onChange={(e) => setRecipientId(e.target.value)}
              placeholder="在用户/咨询师详情页可查看 ID"
              className="mt-1 w-full border rounded-xl px-4 py-2"
            />
          </label>
        </div>

        <label className="block text-sm">
          <span className="text-gray-500">标题</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="例如：请完善资料并完成认证"
            className="mt-1 w-full border rounded-xl px-4 py-2"
          />
        </label>

        <label className="block text-sm">
          <span className="text-gray-500">内容</span>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            placeholder="例如：请上传身份证照片，完善个人资料后提交认证审核。"
            className="mt-1 w-full border rounded-xl px-4 py-2"
          />
        </label>

        <div className="grid md:grid-cols-2 gap-4">
          <label className="block text-sm">
            <span className="text-gray-500">消息类型</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full border rounded-xl px-4 py-2"
            >
              {CATEGORY_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="text-gray-500">跳转链接（可选）</span>
            <select
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              className="mt-1 w-full border rounded-xl px-4 py-2"
            >
              {LINK_PRESETS.map((item) => (
                <option key={item.label} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <button
          type="submit"
          disabled={sending}
          className="px-6 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-60"
        >
          {sending ? "发送中..." : "发送系统消息"}
        </button>
      </form>

      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <div className="p-6 border-b flex items-center justify-between gap-4 flex-wrap">
          <h2 className="text-lg font-bold text-gray-800">发送记录</h2>
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setPage(1);
            }}
            className="border rounded-xl px-4 py-2 text-sm"
          >
            <option value="">全部类型</option>
            <option value="user">发给会员</option>
            <option value="consultant">发给咨询师</option>
          </select>
        </div>

        {loading ? (
          <div className="p-8 text-gray-400">加载中...</div>
        ) : history.length === 0 ? (
          <div className="p-8 text-gray-400">暂无发送记录</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left p-4">时间</th>
                  <th className="text-left p-4">接收方</th>
                  <th className="text-left p-4">标题</th>
                  <th className="text-left p-4">类型</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {history.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="p-4 text-gray-500 whitespace-nowrap">
                      {item.created_at
                        ? new Date(item.created_at).toLocaleString()
                        : "-"}
                    </td>
                    <td className="p-4">
                      {item.recipient_type === "consultant" ? "咨询师" : "会员"}{" "}
                      #{item.recipient_id}
                    </td>
                    <td className="p-4">
                      <p className="font-medium text-gray-800">{item.title}</p>
                      <p className="text-gray-500 line-clamp-2 mt-1">
                        {item.content}
                      </p>
                    </td>
                    <td className="p-4 text-gray-500">{item.category}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="p-4 border-t flex justify-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-4 py-2 rounded-xl border disabled:opacity-40"
            >
              上一页
            </button>
            <span className="px-4 py-2 text-sm text-gray-500">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-4 py-2 rounded-xl border disabled:opacity-40"
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
